import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { trackEvent } from '~/api/analytics';
import { Box, CustomSpinner, DataGrid, Pagination } from '~/commonComponents';
import { DashboardJobsStatus, DashboardJobsType, IDashboardJob } from '~/domain/DashboardJobs';
import { useUserSelector } from '~/hooks';
import { FilterStatusEnum } from '~/utils/Enum';
import { useJobList } from '../../hooks';
import { DataGridCommonColumn } from '../DataGrid.column';
import { EmptyMessage } from '../EmptyMessage';

interface IOpenTabProps {
  jobType: DashboardJobsType;
}

export const OpenTab = ({ jobType }: IOpenTabProps) => {
  const currentUser = useUserSelector();
  const navigate = useNavigate();

  const { data, isInitialLoading, isLoading, methods, pagination } = useJobList({
    status: DashboardJobsStatus.OPEN,
    jobType,
  });

  const handleRowClick = useCallback(
    (item: IDashboardJob) => {
      trackEvent('Shifts - Open Shifts Tab - Shift Tile Clicked', {
        shiftId: item?.id,
        shiftSpecialty: item?.specialty,
        procedures: item?.procedures,
        breakTime: item?.lunchBreakTime,
        autoConfirm: item?.autoFill,
        softwares: currentUser?.office_info?.softwares,
        assistedHygiene: item?.isAssistedHygiene,
        tpp: item?.amount,
      });

      navigate(`/dashboard/job/${item.id}`);
    },
    [currentUser?.office_info?.softwares, navigate],
  );

  if (isInitialLoading) {
    return (
      <Box marginTop={32}>
        <CustomSpinner isLoading size={24} />
      </Box>
    );
  }

  return (
    <DataGrid
      EmptyComponent={<EmptyMessage type={FilterStatusEnum.OPEN} />}
      PaginationComponent={
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={methods.setCurrentPage}
          maxButtons={5}
        />
      }
      columns={[
        DataGridCommonColumn.Office,
        {
          ...DataGridCommonColumn.Type,
          width: '180px',
        },
        DataGridCommonColumn.Details,
        DataGridCommonColumn.Profession,
        DataGridCommonColumn.Rate,
        DataGridCommonColumn.Invite,
        DataGridCommonColumn.Action,
      ]}
      data={data}
      isLoading={isLoading}
      keyExtractor={(item) => item.id}
      onRowClick={handleRowClick}
    />
  );
};
